/**
 * Knowledge Base API
 * ==================
 * API functions for knowledge base management
 */

import apiClient from './client';

const KB_BASE = '/api/v1/knowledge-base';

/**
 * Get all knowledge base categories
 *
 * @returns {Promise<Object>} List of categories with entry counts
 */
export const getCategories = async () => {
  const response = await apiClient.get(`${KB_BASE}/categories`);
  return response.data;
};

/**
 * Get knowledge base entries
 *
 * @param {string|null} category - Optional category filter
 * @returns {Promise<Object>} List of knowledge base entries
 */
export const getEntries = async (category = null) => {
  const params = category ? { category } : {};
  const response = await apiClient.get(`${KB_BASE}/entries`, { params });
  return response.data;
};

/**
 * Create a new knowledge base entry
 *
 * @param {Object} entryData - Entry data
 * @param {string} entryData.title - Entry title
 * @param {string} entryData.category - Entry category
 * @param {string} entryData.content - Entry content
 * @param {Array<string>} [entryData.tags] - Optional tags
 * @param {Object} [entryData.metadata] - Optional metadata
 * @returns {Promise<Object>} Created entry data
 */
export const createEntry = async (entryData) => {
  const response = await apiClient.post(`${KB_BASE}/entries`, entryData);
  return response.data;
};

/**
 * Update an existing knowledge base entry
 *
 * @param {string} entryId - Entry UUID
 * @param {Object} updateData - Entry fields to update
 * @param {string} [updateData.title] - Entry title
 * @param {string} [updateData.category] - Entry category
 * @param {string} [updateData.content] - Entry content
 * @param {Array<string>} [updateData.tags] - Tags
 * @param {Object} [updateData.metadata] - Metadata
 * @returns {Promise<Object>} Updated entry data
 */
export const updateEntry = async (entryId, updateData) => {
  const response = await apiClient.put(`${KB_BASE}/entries/${entryId}`, updateData);
  return response.data;
};

/**
 * Delete a knowledge base entry
 *
 * @param {string} entryId - Entry UUID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteEntry = async (entryId) => {
  const response = await apiClient.delete(`${KB_BASE}/entries/${entryId}`);
  return response.data;
};

const knowledgeBaseAPI = {
  getCategories,
  getEntries,
  createEntry,
  updateEntry,
  deleteEntry,
};

export default knowledgeBaseAPI;
